/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type FormEventHandler, useEffect, useState } from 'react';

import { css, jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditorSearchIcon from '@atlaskit/icon/core/search';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { ANALYTIC_TYPE, ANALYTICS_CHANNEL, EVENT_TYPE } from '../../../utils/analytics';

import { messages } from './constants';
import type { EnterpriseSearchBarProps } from './types';

const textFieldBoxStyles = xcss({
	justifySelf: 'center',
	marginTop: 'space.200',
});

const cssOverrideWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'> div': {
		height: '40px',
		borderWidth: token('border.width'),
		borderRadius: token('radius.medium'),
	},
});

const searchIconWrapperStyles = xcss({
	minWidth: 'size.200',
	minHeight: 'size.200',
	marginLeft: 'space.100',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const clearIconStyles = xcss({
	marginRight: 'space.100',
});

export function SearchBar({
	testId,
	width,
	textQueryParam,
	updateQueryParameters,
}: EnterpriseSearchBarProps): JSX.Element {
	const [localQuery, setLocalQuery] = useState(textQueryParam ?? '');
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		setLocalQuery(textQueryParam ?? '');
	}, [textQueryParam]);

	const handleOnSubmit = () => {
		// update params and trigger a new search
		if (updateQueryParameters) {
			// pass updated text param to callback
			// params for all other filters are handled in updateSearchParams
			updateQueryParameters({ text: localQuery });
		}
	};

	const onChange: FormEventHandler<HTMLInputElement> = (e) => {
		setLocalQuery(e.currentTarget.value);
	};

	const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && localQuery.trim() !== '') {
			handleOnSubmit();
			const searchEvent = createAnalyticsEvent({
				type: ANALYTIC_TYPE.SEND_TRACK_EVENT,
				eventType: EVENT_TYPE.TRACK,
				source: ANALYTICS_CHANNEL,
				action: 'submitted',
				actionSubject: 'subsequentSearch',
				actionSubjectId: 'searchBar',
			});
			searchEvent.fire(ANALYTICS_CHANNEL);
		}
	};

	return (
		<Box xcss={textFieldBoxStyles} testId={testId}>
			<div
				css={cssOverrideWrapperStyles}
				style={{
					width,
				}}
			>
				<Textfield
					id="text-query-input"
					testId="text-query-input"
					value={localQuery}
					onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => onKeyPress(event)}
					onChange={onChange}
					autoComplete="off"
					placeholder={messages.inputPlaceholder.defaultMessage}
					elemBeforeInput={
						<Box xcss={searchIconWrapperStyles}>
							<EditorSearchIcon
								color="currentColor"
								spacing="spacious"
								label={messages.searchIconLabel.defaultMessage}
							/>
						</Box>
					}
					elemAfterInput={
						localQuery && (
							<Box xcss={clearIconStyles}>
								<IconButton
									icon={(iconProps) => (
										<CrossIcon
											{...iconProps}
											label={messages.clearIconLabel.defaultMessage}
											color="currentColor"
										/>
									)}
									spacing={'compact'}
									onClick={() => setLocalQuery('')}
									label={messages.clearIconLabel.defaultMessage}
									appearance={'subtle'}
								/>
							</Box>
						)
					}
				/>
			</div>
		</Box>
	);
}
